<template>
    <div>
        <div class="mt-md-4 mt-sm-2 mb-2 text-center">
            <h2>{{$t('historicalData')}}</h2>
        </div>
        <kendo-datasource ref="datasource"
                          :transport="transportSettings"
                          :schema-model-id="'id'"
                          :schema-model-fields="schemaModelFields"
                          :schema-total="'total'"
                          :schema-data="'data'"
                          :page-size='defaultGridRows'
                          :server-paging="true"
                          :server-sorting="true"
                          :server-filtering="true"
                          :error="dataSourceError"
                          :requestEnd="datasorceRequestEnd">
        </kendo-datasource>

        <kendo-grid ref="grid"
                    :id="'historyRecordsGrid'"
                    :data-source-ref="'datasource'"
                    :columns="columns"
                    :pageable="gridOptions.pageable"
                    :navigatable="gridOptions.navigatable"
                    :editable="gridOptions.editable"
                    :filterable="gridOptions.filterable"
                    :reorderable="gridOptions.reorderable"
                    :resizable="gridOptions.resizable"
                    :groupable="gridOptions.groupable"
                    :allow-copy="gridOptions.allowCopy"
                    :column-menu="gridOptions.columnMenu"
                    :sortable-mode="gridOptions.sortable.mode"
                    :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                    :sortable-show-indexes="gridOptions.sortable.showIndexes"
                    :selectable="gridOptions.selectable"
                    :scrollable="gridOptions.scrollable"
                    :toolbar="toolbar"
                    :excel="gridOptions.excel"
                    :pdf="gridOptions.pdf"
                    :dataBound="dataBound">
        </kendo-grid>
    </div>

</template>

<script>
    import JSZip from 'jszip'
    import { mapGetters } from 'vuex';

    /*
    Renders the grid in "DejaVu Sans" font family, which is
    declared in kendo.common.css. It also declares the paths to the
    fonts below using <tt>kendo.pdf.defineFont</tt>, because the
    stylesheet is hosted on a different domain.
    */
    kendo.pdf.defineFont({
        "DejaVu Sans": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans.ttf",
        "DejaVu Sans|Bold": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Bold.ttf",
        "DejaVu Sans|Bold|Italic": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf",
        "DejaVu Sans|Italic": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf",
        "WebComponentsIcons": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/glyphs/WebComponentsIcons.ttf"
    });

    export default {
        components: {
        },
        props: {
            id: {
                type: [Number, String],
                required: true
            },
            url: {
                type: String,
                required: true
            },
            columns: {
                type: Array,
                required: true
            },
            schemaModelFields: {
                type: Object,
                required: true
            },
            componentName: {
                type: String,
                default: 'PersonHistory'
            },
        },
        data() {
            return {
                searchQuery: '',
                defaultGridRows: this.$store.getters.defaultGridRows,
                transportSettings: {
                    read: {
                        url: `${this.$store.getters.baseUrl}/${this.url}`,
                        dataType: 'json',
                        type: 'POST',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.parameterMap
                },
                gridOptions: {
                    ...this.$store.getters.gridOptions,
                    excel: {
                        fileName: `${this.componentName}.xlsx`,
                        allPages: false,
                        filterable: true,
                        collapsible: true
                    },
                    pdf: {
                        fileName: `${this.pdf}.xlsx`,
                        title: this.$t(this.componentName),
                        subject: this.$t(this.titleKey),
                        creator: this.$store.userName,
                        allPages: false,
                        avoidLinks: true,
                        paperSize: 'A4',
                        margin: { top: '2cm', left: '1cm', right: '1cm', bottom: '1cm' },
                        landscape: true,
                        repeatHeaders: true,
                        scale: 0.6
                    }
                }
            };
        },
        methods: {
            dataBound(e) {
            },
            parameterMap: function (options, operation) {
                if (operation !== 'read' && options.models) {
                    return { models: kendo.stringify(options.models) }
                } else {
                    let newOptions = { id: this.id, request: options, };
                    return kendo.stringify(newOptions);
                }
            },
            beforeSend: function (req) {
                if (this.authToken) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.authToken);
                }
            },
            refresh() {
                const ds = this.$refs.datasource.kendoWidget();
                if (ds) {
                    ds.read();
                }
            },
            datasorceRequestEnd(e) {
            },
            dataSourceError(e) {
                this.$vs.notify({
                    title: this.$t('notifications.error'),
                    text: this.$helpers.kendoDataSourceErrorText(e),
                    color: 'danger',
                    time: this.$store.getters.notificationDuration,
                });
            },
            clearFilters() {
                this.searchQuery = '';
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    grid.dataSource.sort({});
                    grid.dataSource.page(1);
                    grid.dataSource.aggregate([]);
                    grid.dataSource.filter({});
                }
            },
            massSearch() {
                if (!this.searchQuery) {
                    this.$vs.notify({
                        title: this.$t('buttons.search.btnText'),
                        text: this.$t('emptySearchFilter'),
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    })

                    return;
                }

                let grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    let filter = this.$helpers.getMassFilterForGrid(grid, this.searchQuery);
                    if (filter) {
                        grid.dataSource.filter(filter);
                    }
                }
            },
        },
        mounted() {
        },
        created: function () {
            window.JSZip = JSZip // Необходимо е за да работи експорта в excel
        },
        computed: {
            ...mapGetters(['authToken', 'isEnVersion']),
            toolbar: function () {
                return [{ name: 'excel', text: this.$t('buttons.excelExport.tooltipText') },
                { name: 'pdf', text: this.$t('buttons.pdfExport.tooltipText') }]
            },
        }
    }
</script>

<style scoped>
    .btn {
        margin-right: 10px;
        margin-left: 5px;
    }

    .checkbox {
        margin-right: 5px;
    }

    .selectFile {
        display: none;
    }

    .inlineForm {
        display: inline;
    }

    /*
        Use the DejaVu Sans font for display and embedding in the PDF file.
        The standard PDF fonts have no support for Unicode characters.
    */
    .k-pdf-export .k-grid {
        font-family: "DejaVu Sans", "Arial", sans-serif;
    }

    .k-pdf-export .k-filterable .k-grid-filter {
        display: none;
    }

    .k-pdf-export .k-with-icon .k-header-column-menu {
        display: none;
    }

    .k-grid .k-grid-header .k-header .k-link {
        height: auto;
    }

    .k-grid .k-grid-header .k-header {
        white-space: normal;
    }

    .k-grid-edit, .k-grid-details, .k-grid-delete, .k-grid-pdf, .k-grid-excel {
        padding: 0;
        margin-bottom: 2px;
    }
</style>
